import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import type { SelectOption } from '@/shared/ui/selectTypes';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchActivePrizeGroups, fetchPrizeOptions, setActivePrizes, SECTOR_COUNT } from './api';

const SAVE_CONFIRM = 'Вы уверены, что хотите сохранить изменения?';

type SectorsFormProps = {
  options: SelectOption[];
  initial: string[][];
};

const SectorsForm = ({ options, initial }: SectorsFormProps) => {
  const qc = useQueryClient();
  const [groups, setGroups] = useState(initial);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => setActivePrizes(groups),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['raffle-prizes'] });
    },
  });

  /** A sector is editable once the previous one has prizes; clearing a sector clears the following ones. */
  const setGroup = (index: number, ids: string[]) => {
    setSaved(false);
    setGroups((prev) =>
      prev.map((current, i) => {
        if (i === index) return ids;
        return i > index && ids.length === 0 ? [] : current;
      }),
    );
  };

  const availableFor = (index: number) => {
    const taken = groups.filter((_, i) => i !== index).flat();
    return options.filter((option) => !taken.includes(option.value));
  };

  const isComplete = groups.every((ids) => ids.length > 0);

  return (
    <div style={{ display: 'flex', gap: 32, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
      {groups.map((ids, index) => (
        <div key={index}>
          <b style={{ display: 'block', marginBottom: 8 }}>{`Приз №${index + 1}`}</b>
          <MultiSelect
            options={availableFor(index)}
            value={ids}
            placeholder="Выбрать"
            isDisabled={index > 0 && groups[index - 1].length === 0}
            width={248}
            onChange={(next) => setGroup(index, next)}
          />
        </div>
      ))}
      <button
        type="button"
        className="btn btn-warning"
        disabled={!isComplete || save.isPending}
        onClick={() => window.confirm(SAVE_CONFIRM) && save.mutate()}
      >
        Сохранить
      </button>
      {saved && <span className="text-green">Данные сохранены успешно</span>}
      {save.isError && <span className="text-red">Не удалось сохранить призы</span>}
    </div>
  );
};

/** "Выбор призов для секторов колеса" (legacy `wheel_fortune/prize/set-active`). */
export const PrizeSectorsPanel = () => {
  const options = useQuery({ queryKey: ['raffle-prize-options'], queryFn: fetchPrizeOptions });
  const active = useQuery({ queryKey: ['raffle-active-prizes'], queryFn: fetchActivePrizeGroups });

  return (
    <div style={{ marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f4f4f4' }}>
      <h4>Выбор призов для секторов колеса</h4>
      <QueryStatus
        isLoading={options.isLoading || active.isLoading}
        error={options.error ?? active.error}
        errorText="Ошибка загрузки призов для секторов"
      />
      {options.data && active.data && active.data.length === SECTOR_COUNT && (
        <SectorsForm options={options.data} initial={active.data} />
      )}
    </div>
  );
};
